import React, { useEffect, useRef, useState } from "react";
import { X, Terminal, Download, Play, ImageIcon, AlertTriangle } from "lucide-react";
import { getJob, screenshotUrl, certUrl, certParts, PART_LABEL, registerJob } from "../api";
import StatusBadge from "./StatusBadge";

export default function JobConsoleModal({ jobId, open, onClose, onChanged }) {
  const [job, setJob] = useState(null);
  const [preview, setPreview] = useState(null);
  const logEndRef = useRef(null);

  useEffect(() => {
    if (!open || !jobId) return;
    let active = true;
    const load = async () => {
      try {
        const data = await getJob(jobId);
        if (active) setJob(data);
      } catch (e) { /* ignore */ }
    };
    load();
    const t = setInterval(load, 2500);
    return () => { active = false; clearInterval(t); };
  }, [open, jobId]);

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [job?.logs?.length]);

  if (!open || !job) return null;

  const running = job.status === "in_progress";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="job-console-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 flex max-h-[88vh] w-full max-w-3xl flex-col rounded-2xl border border-slate-700 bg-[#161f32] shadow-2xl fade-up">
        {/* header */}
        <div className="flex items-center justify-between border-b border-slate-800 px-6 py-4">
          <div>
            <h2 className="font-display text-lg font-semibold text-slate-100">
              {job.first_name} {job.last_name}
            </h2>
            <p className="text-xs text-slate-500">
              {job.job_number ? <span className="font-mono text-slate-400">Job {job.job_number} · </span> : null}
              {job.address1}, {job.city} {job.state} {job.zip}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <StatusBadge status={job.status} />
            <button data-testid="console-close" onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-800 hover:text-slate-200">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="grid flex-1 grid-cols-1 gap-0 overflow-hidden md:grid-cols-5">
          {/* console */}
          <div className="col-span-3 flex flex-col overflow-hidden border-r border-slate-800">
            <div className="flex items-center gap-2 border-b border-slate-800 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-400">
              <Terminal className="h-3.5 w-3.5" /> Automation Console
              {running && <span className="ml-auto flex items-center gap-1.5 text-blue-400"><span className="h-2 w-2 animate-pulse rounded-full bg-blue-400" />{job.step}</span>}
            </div>
            <div data-testid="console-logs" className="flex-1 overflow-y-auto bg-[#0a0e17] p-4 font-mono text-xs leading-relaxed">
              {(job.logs || []).length === 0 && <p className="text-slate-600">No activity yet. Trigger registration to begin.</p>}
              {(job.logs || []).map((l, i) => (
                <div key={i} className="flex gap-2 text-slate-300">
                  <span className="shrink-0 text-slate-600">{new Date(l.ts).toLocaleTimeString()}</span>
                  <span className={l.msg.toLowerCase().includes("fail") || l.msg.toLowerCase().includes("not recognized") ? "text-rose-400" : (l.msg.toLowerCase().includes("added") || l.msg.toLowerCase().includes("success") ? "text-emerald-400" : "text-slate-300")}>
                    {l.msg}
                  </span>
                </div>
              ))}
              <div ref={logEndRef} />
            </div>
            {(job.status === "failed" || job.status === "needs_review") && job.error && (
              <div className={`border-t px-4 py-2 text-xs ${job.status === "needs_review" ? "border-amber-500/20 bg-amber-500/5 text-amber-300" : "border-rose-500/20 bg-rose-500/5 text-rose-300"}`}>
                <div className="flex items-start gap-2"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /><span>{job.error}</span></div>
              </div>
            )}
          </div>

          {/* side: equipment + screenshots */}
          <div className="col-span-2 flex flex-col overflow-y-auto p-4">
            <div className="mb-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Equipment ({job.serials?.length || 0})</p>
              <div className="space-y-1.5">
                {(job.serials || []).map((s, i) => (
                  <div key={i} data-testid={`console-serial-${i}`} className="rounded border border-slate-700/60 bg-slate-900/80 px-2 py-1 font-mono text-xs font-semibold tracking-wider text-emerald-400">
                    {s}
                  </div>
                ))}
              </div>
              <p className="mt-2 text-xs text-slate-500">Install date: {job.install_date || "—"}</p>
            </div>

            {(job.screenshots || []).length > 0 && (
              <div>
                <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-400">
                  <ImageIcon className="h-3.5 w-3.5" /> Step Snapshots
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {job.screenshots.map((name) => (
                    <button key={name} onClick={() => setPreview(screenshotUrl(jobId, name))} className="overflow-hidden rounded border border-slate-700 hover:border-blue-500">
                      <img src={screenshotUrl(jobId, name)} alt={name} className="h-16 w-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* footer actions */}
        <div className="flex items-center justify-end gap-2 border-t border-slate-800 px-6 py-3">
          {job.status !== "in_progress" && job.status !== "registered" && (
            <button
              data-testid="console-register-button"
              onClick={async () => { await registerJob(jobId); onChanged && onChanged(); }}
              className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500"
            >
              <Play className="h-4 w-4" /> {job.status === "failed" ? "Retry Registration" : "Run Registration"}
            </button>
          )}
          {(job.status === "registered" || job.status === "needs_review") && certParts(job).map((part) => (
            <a
              key={part}
              data-testid={`console-download-cert-${part}`}
              href={certUrl(jobId, part)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-500"
            >
              <Download className="h-4 w-4" /> {PART_LABEL[part] || part} PDF
            </a>
          ))}
        </div>
      </div>

      {preview && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/85 p-6" onClick={() => setPreview(null)}>
          <img src={preview} alt="snapshot" className="max-h-[90vh] max-w-[90vw] rounded-lg border border-slate-700" />
        </div>
      )}
    </div>
  );
}
